// 通过 Nginx /kb/ 代理访问 KB Server（避免 Mixed Content）
const KB_BASE = '/kb';
const KB_TOKEN = 'kb_H7ZYRcNO1BPW5Bxu';

async function kbRequest(path: string, options: RequestInit = {}) {
  const url = `${KB_BASE}${path}`;
  const res = await fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      'Authorization': `Bearer ${KB_TOKEN}`,
    },
  });
  const data = await res.json();
  if (!res.ok || data.success === false) {
    throw new Error(data.error || `HTTP ${res.status}`);
  }
  return data.data ?? data;
}

export const kbApi = {
  // Health (no auth)
  health: () => fetch(`${KB_BASE}/health`).then(r => r.json()),

  // Stats
  stats: () => kbRequest('/api/stats'),
  statsDetailed: () => kbRequest('/api/stats/detailed'),
  statsStatus: () => kbRequest('/api/stats/status'),

  // Documents
  listDocuments: () => kbRequest('/api/documents'),
  // 服务端分页列表：返回 { items, total, page, pageSize, totalAll, categoryCounts }
  listDocumentsPaged: (params: {
    page: number; pageSize: number; q?: string; category?: string;
    excludeCats?: string[]; excludePrefixes?: string[];
  }) => {
    const sp = new URLSearchParams({ page: String(params.page), pageSize: String(params.pageSize) });
    if (params.q) sp.set('q', params.q);
    if (params.category) sp.set('category', params.category);
    if (params.excludeCats && params.excludeCats.length > 0) sp.set('excludeCats', params.excludeCats.join(','));
    if (params.excludePrefixes && params.excludePrefixes.length > 0) sp.set('excludePrefixes', params.excludePrefixes.join(','));
    return kbRequest(`/api/documents?${sp.toString()}`);
  },
  uploadDocument: (file: File, categoryId?: string) => {
    const form = new FormData();
    form.append('file', file);
    if (categoryId) form.append('categoryId', categoryId);
    return kbRequest('/api/documents', { method: 'POST', body: form });
  },
  // SSE流式上传（返回进度事件）
  uploadDocumentStream: (file: File, categoryId: string | undefined, onProgress: (data: any) => void, signal?: AbortSignal): Promise<any> => {
    return new Promise((resolve, reject) => {
      const form = new FormData();
      form.append('file', file);
      if (categoryId) form.append('categoryId', categoryId);
      
      const startTime = Date.now();
      console.log(`[KB-UPLOAD] 开始上传: ${file.name}, size=${file.size}, categoryId=${categoryId}`);
      
      fetch(`${KB_BASE}/api/documents/stream`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${KB_TOKEN}` },
        body: form,
        signal,
      }).then(async (res) => {
        console.log(`[KB-UPLOAD] ${file.name} 收到响应: status=${res.status}, ok=${res.ok}`);
        if (!res.ok) { 
          console.error(`[KB-UPLOAD] ${file.name} HTTP错误: ${res.status}`);
          reject(new Error(`HTTP ${res.status}`)); 
          return; 
        }
        const reader = res.body?.getReader();
        if (!reader) { 
          console.error(`[KB-UPLOAD] ${file.name} 无法读取响应body`);
          reject(new Error('无法读取响应')); 
          return; 
        }
        const decoder = new TextDecoder();
        let buffer = '';
        let currentEvent = '';
        let lastProgressTime = Date.now();
        
        while (true) {
          const { done, value } = await reader.read();
          if (done) { 
            const elapsed = Date.now() - startTime;
            console.warn(`[KB-UPLOAD] ${file.name} 流结束(done=true), 耗时${elapsed}ms, buffer剩余="${buffer.slice(0,200)}"`);
            // 流正常结束但没收到complete事件 → 可能是服务端崩了
            reject(new Error('上传超时：服务端连接断开')); 
            return; 
          }
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          
          for (let i = 0; i < lines.length; i++) {
            const line = lines[i].trim();
            if (!line) continue;
            if (line.startsWith('event: ')) {
              currentEvent = line.slice(7).trim();
            } else if (line.startsWith('data: ')) {
              if (!currentEvent) continue;
              try {
                const data = JSON.parse(line.slice(6).trim());
                const now = Date.now();
                if (now - lastProgressTime > 10000) {
                  console.warn(`[KB-UPLOAD] ${file.name} 进度间隔过大: ${now - lastProgressTime}ms`);
                }
                lastProgressTime = now;
                if (currentEvent === 'progress') onProgress(data);
                else if (currentEvent === 'complete') { 
                  const elapsed = Date.now() - startTime;
                  console.log(`[KB-UPLOAD] ${file.name} 上传完成, chunks=${data.chunks}, 耗时${elapsed}ms`);
                  resolve(data); 
                  return; 
                }
                else if (currentEvent === 'error') {
                  const elapsed = Date.now() - startTime;
                  console.error(`[KB-UPLOAD] ${file.name} 服务端返回错误: ${data.error || data.message}, 耗时${elapsed}ms`);
                  reject(new Error(data.error || data.message || '上传失败'));
                  return;
                }
                else if (currentEvent === 'cancelled') {
                  // 服务端确认已中断(用户点中断→客户端断开→服务端停止向量化):按取消处理,不算失败
                  console.log(`[KB-UPLOAD] ${file.name} 服务端已确认中断`);
                  reject(new Error('__CANCELLED__'));
                  return;
                }
              } catch (e: any) {
                console.warn(`[KB-UPLOAD] ${file.name} SSE JSON解析失败: ${e.message}, line="${line.slice(0, 100)}"`);
              }
              currentEvent = '';
            }
          }
        }
      }).catch((e) => {
        console.error(`[KB-UPLOAD] ${file.name} fetch异常: ${e.message}`);
        reject(e);
      });
    });
  },
  deleteDocument: (filename: string) =>
    kbRequest(`/api/documents/${encodeURIComponent(filename)}`, { method: 'DELETE' }),
  checkDuplicates: (filenames: string[]) =>
    kbRequest('/api/documents/check-duplicates', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filenames }),
    }),
  getDocumentChunks: (filename: string) =>
    kbRequest(`/api/documents/${encodeURIComponent(filename)}/chunks`),
  setDocCategory: (filename: string, categoryId: string) =>
    kbRequest(`/api/documents/${encodeURIComponent(filename)}/category`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ categoryId }),
    }),

  // Search
  search: (query: string, limit = 10, categoryIds?: string[]) =>
    kbRequest('/api/search', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query, limit, categoryIds }),
    }),

  // Categories
  listCategories: () => kbRequest('/api/categories'),
  createCategory: (name: string, description?: string, color?: string) =>
    kbRequest('/api/categories', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, description, color }),
    }),
  updateCategory: (id: string, data: any) =>
    kbRequest(`/api/categories/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),
  deleteCategory: (id: string) =>
    kbRequest(`/api/categories/${id}`, { method: 'DELETE' }),

  // Token verification
  verifyToken: () => kbRequest('/api/auth/verify'),
  tokenInfo: () => kbRequest('/api/auth/info'),

  // 经验知识库
  experience: {
    search: (query: string, limit = 5, userId?: string) =>
      kbRequest('/api/experience/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query, limit, userId }),
      }),
    list: (page = 1, pageSize = 20, userId?: string) => {
      let url = `/api/experience/list?page=${page}&pageSize=${pageSize}`;
      if (userId) url += `&userId=${encodeURIComponent(userId)}`;
      return kbRequest(url);
    },
    get: (id: string, userId?: string) => {
      let url = `/api/experience/entry/${id}`;
      if (userId) url += `?userId=${encodeURIComponent(userId)}`;
      return kbRequest(url);
    },
    delete: (id: string) =>
      kbRequest(`/api/experience/entry/${id}`, { method: 'DELETE' }),
    stats: (userId?: string) => {
      let url = '/api/experience/stats';
      if (userId) url += `?userId=${encodeURIComponent(userId)}`;
      return kbRequest(url);
    },
  },

  // 个人工作知识库：必须传当前登录用户ID，KB服务按 ownerUserId 强隔离
  personalWork: {
    list: (page = 1, pageSize = 20, userId: string) =>
      kbRequest(`/api/personal-work/list?page=${page}&pageSize=${pageSize}&userId=${encodeURIComponent(userId)}`),
    stats: (userId: string) =>
      kbRequest(`/api/personal-work/stats?userId=${encodeURIComponent(userId)}`),
    delete: (id: string, userId: string) =>
      kbRequest(`/api/personal-work/entry/${id}?userId=${encodeURIComponent(userId)}`, { method: 'DELETE' }),
  },

  // 团队工作知识库：全团队共享数据，页面入口由管理端按 admin 控制
  teamWork: {
    list: (page = 1, pageSize = 20, filters: { targetId?: string; startTime?: number; endTime?: number } = {}) => {
      const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
      if (filters.targetId) params.set('targetId', filters.targetId);
      if (filters.startTime) params.set('startTime', String(filters.startTime));
      if (filters.endTime) params.set('endTime', String(filters.endTime));
      return kbRequest(`/api/team-work/list?${params.toString()}`);
    },
    stats: () => kbRequest('/api/team-work/stats'),
    delete: (id: string) =>
      kbRequest(`/api/team-work/entry/${id}`, { method: 'DELETE' }),
  },

  // ── 超大文件上传通道（独立接口）──
  largeUpload: {
    /** 创建任务，立即返回 taskId 和初始快照 */
    create: (file: File, categoryId: string | undefined, onUploadProgress?: (pct: number) => void): Promise<any> => {
      return new Promise((resolve, reject) => {
        const form = new FormData();
        form.append('file', file);
        if (categoryId) form.append('categoryId', categoryId);

        // 用 XHR 才能拿到上传字节进度（fetch没法）
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${KB_BASE}/api/documents/large`);
        xhr.setRequestHeader('Authorization', `Bearer ${KB_TOKEN}`);
        xhr.upload.onprogress = (e) => {
          if (onUploadProgress && e.lengthComputable) {
            onUploadProgress(Math.round((e.loaded / e.total) * 100));
          }
        };
        xhr.onload = () => {
          try {
            const json = JSON.parse(xhr.responseText);
            if (xhr.status >= 200 && xhr.status < 300 && json.success !== false) {
              resolve(json.data ?? json);
            } else {
              reject(new Error(json.error || `HTTP ${xhr.status}`));
            }
          } catch (e: any) {
            reject(new Error(`响应解析失败: ${e.message}`));
          }
        };
        xhr.onerror = () => reject(new Error('网络错误'));
        xhr.ontimeout = () => reject(new Error('上传超时'));
        // 上传阶段超时给 5 分钟（100MB 在 10MB/s 下约10秒，500秒余量足够）
        xhr.timeout = 5 * 60 * 1000;
        xhr.send(form);
      });
    },

    /** 获取任务快照（用于断线后恢复） */
    getStatus: (taskId: string) => kbRequest(`/api/documents/large/${taskId}`),

    /** 列出所有大上传任务 */
    list: () => kbRequest('/api/documents/large/list'),

    /** 取消任务 */
    cancel: (taskId: string) =>
      kbRequest(`/api/documents/large/${taskId}`, { method: 'DELETE' }),

    /**
     * SSE 订阅进度。返回 { close } 用于主动断开。
     * onUpdate 收到完整 task 对象；onDone 终态触发；onError 异常触发。
     */
    subscribe(
      taskId: string,
      onUpdate: (task: any) => void,
      onDone?: (task: any) => void,
      onError?: (err: Error) => void,
    ): { close: () => void } {
      // EventSource 不支持自定义 header（无法带 Bearer）→ 改用 fetch+ReadableStream
      const ctrl = new AbortController();
      let closed = false;

      const start = async () => {
        try {
          const res = await fetch(`${KB_BASE}/api/documents/large/${taskId}/stream`, {
            headers: { 'Authorization': `Bearer ${KB_TOKEN}` },
            signal: ctrl.signal,
          });
          if (!res.ok) throw new Error(`SSE HTTP ${res.status}`);
          const reader = res.body?.getReader();
          if (!reader) throw new Error('无法读取SSE响应');
          const decoder = new TextDecoder();
          let buf = '';
          let curEvent = '';

          while (!closed) {
            const { done, value } = await reader.read();
            if (done) break;
            buf += decoder.decode(value, { stream: true });
            const lines = buf.split('\n');
            buf = lines.pop() || '';
            for (const ln of lines) {
              const line = ln.trim();
              if (!line || line.startsWith(':')) continue; // 跳过心跳注释
              if (line.startsWith('event: ')) {
                curEvent = line.slice(7).trim();
              } else if (line.startsWith('data: ')) {
                if (!curEvent) continue;
                try {
                  const data = JSON.parse(line.slice(6).trim());
                  if (curEvent === 'snapshot' || curEvent === 'progress') {
                    onUpdate(data);
                  } else if (curEvent === 'complete' || curEvent === 'failed' || curEvent === 'cancelled') {
                    onUpdate(data);
                    onDone?.(data);
                    closed = true;
                    return;
                  }
                } catch (e: any) {
                  console.warn(`[KB-LARGE-SSE] JSON解析失败: ${e.message}`);
                }
                curEvent = '';
              }
            }
          }
        } catch (e: any) {
          if (e.name !== 'AbortError') {
            console.error(`[KB-LARGE-SSE] 异常: ${e.message}`);
            onError?.(e);
          }
        }
      };

      start();
      return {
        close: () => { closed = true; ctrl.abort(); },
      };
    },
  },
};
